"use client";

import { useState, useRef, useCallback, useEffect } from "react";
import { useParams, usePathname } from "next/navigation";
import { ensureOrgHref } from "@/hooks/useOrgHref";
import { chatAuthHeaders } from "@/lib/chatHeaders";
import { useAuth } from "@/lib/auth";
import { useChatPageStateSnapshot } from "@/contexts/ChatPageStateContext";
import { forgetConversation, rememberConversation } from "@/lib/amandaConversation";

const API_BASE = "/api";

export interface ChatMessage {
  id: string;
  role: "user" | "assistant";
  content: string;
  actions?: ActionEvent[];
}

export interface ActionEvent {
  id: string;
  action: string;
  data: Record<string, unknown>;
}

export interface ChatQuota {
  used: number;
  limit: number | null;
  remaining: number | null;
}

export interface Conversation {
  id: string;
  title: string | null;
  status: string;
  message_count: number;
}

export interface UploadedFile {
  file_ref: string;
  filename: string;
  storage_key: string;
}

interface UseAmandaChatOptions {
  /** Extra context appended to each message (e.g. envelope_id for editor panel) */
  extraContext?: Record<string, unknown>;
  /** Callback when a document_revised action is received */
  onDocumentRevised?: (markdown: string, version: number) => void;
  /** Callback when a navigate_to action is received */
  onNavigateTo?: (url: string) => void;
  /** Callback when page_settings_updated action is received */
  onPageSettingsUpdated?: (pageSettings: Record<string, unknown>) => void;
  /** Every V2 typed card as it streams (prepared_action, draft_change, error, …) */
  onCard?: (card: Record<string, unknown>) => void;
  /** Every status line (what Amanda is doing right now); null when it clears */
  onStatus?: (text: string | null) => void;
}

export function useAmandaChat(options: UseAmandaChatOptions = {}) {
  const { isAuthenticated, getAccessToken, activeOrg } = useAuth();
  const pathname = usePathname();
  const getPageState = useChatPageStateSnapshot();
  const params = useParams();
  const orgSlug = (params?.org as string | undefined) ?? activeOrg?.slug ?? null;

  const [activeConversationId, setActiveConversationId] = useState<string | null>(() => {
    if (typeof window === "undefined") return null;
    return sessionStorage.getItem("sf_active_conversation_id");
  });
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [actions, setActions] = useState<ActionEvent[]>([]);
  const [isStreaming, setIsStreaming] = useState(false);
  // What Amanda is DOING right now, one line per tool call. V2 buffers the
  // turn's prose behind the honesty rail, so without this the panel is blank
  // from the moment the user hits send until the whole turn resolves.
  const [statusText, setStatusText] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [quota, setQuota] = useState<ChatQuota | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [isUploading, setIsUploading] = useState(false);

  const abortControllerRef = useRef<AbortController | null>(null);
  // Callbacks are read through a ref so a shell that passes inline lambdas
  // (the widget does) never invalidates sendMessage mid-stream.
  const optionsRef = useRef(options);
  optionsRef.current = options;

  // Sync activeConversationId to sessionStorage, and remember it across reloads
  // (30-minute reuse — see lib/amandaConversation) so a trigger or a refresh
  // continues the conversation instead of orphaning the draft it just made.
  useEffect(() => {
    if (activeConversationId) {
      sessionStorage.setItem("sf_active_conversation_id", activeConversationId);
      rememberConversation(activeConversationId);
    } else {
      sessionStorage.removeItem("sf_active_conversation_id");
    }
  }, [activeConversationId]);

  const getToken = useCallback(() => {
    return getAccessToken() || localStorage.getItem("sf_access_token") || "";
  }, [getAccessToken]);

  const loadQuota = useCallback(async () => {
    try {
      const token = getToken();
      const res = await fetch(`${API_BASE}/chat/quota`, {
        headers: chatAuthHeaders(token),
      });
      if (res.ok) {
        const data = await res.json();
        setQuota(data);
      }
    } catch {
      // Silent
    }
  }, [getToken]);

  const loadConversations = useCallback(async () => {
    try {
      const token = getToken();
      const res = await fetch(`${API_BASE}/chat/conversations?limit=20`, {
        headers: chatAuthHeaders(token),
      });
      if (res.ok) {
        const data = await res.json();
        const list: Conversation[] = data.conversations || [];
        setConversations(list);
        return list;
      }
    } catch {
      // Silent
    }
    return [];
  }, [getToken]);

  /** Upload a PDF for Amanda to work on. Validates, sets `error` on failure, returns the file meta. */
  const uploadFile = useCallback(async (file: File): Promise<UploadedFile | null> => {
    if (!file.name.toLowerCase().endsWith(".pdf")) {
      setError("Only PDF files are supported.");
      return null;
    }
    if (file.size > 10 * 1024 * 1024) {
      setError("File too large. Maximum size is 10MB.");
      return null;
    }
    setIsUploading(true);
    setError(null);
    try {
      const token = getToken();
      const formData = new FormData();
      formData.append("file", file);
      const res = await fetch(`${API_BASE}/chat/upload-file`, {
        method: "POST",
        headers: chatAuthHeaders(token),
        body: formData,
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        setError(data.detail || "Upload failed.");
        return null;
      }
      const data = await res.json();
      return { file_ref: data.file_ref, filename: data.filename, storage_key: data.storage_key };
    } catch {
      setError("Upload failed. Please try again.");
      return null;
    } finally {
      setIsUploading(false);
    }
  }, [getToken]);

  /** Continue a known conversation (or start over with null) without a fetch. */
  const resumeConversation = useCallback((conversationId: string | null) => {
    setActiveConversationId(conversationId);
    if (!conversationId) {
      setMessages([]);
      setActions([]);
    }
  }, []);

  const createConversation = useCallback(async (): Promise<string | null> => {
    try {
      const token = getToken();
      const res = await fetch(`${API_BASE}/chat/conversations`, {
        method: "POST",
        headers: {
          ...chatAuthHeaders(token),
          "Content-Type": "application/json",
        },
      });
      if (res.ok) {
        const data = await res.json();
        setActiveConversationId(data.id);
        setMessages([]);
        setActions([]);
        return data.id;
      }
    } catch {
      // Silent
    }
    return null;
  }, [getToken]);

  const loadMessages = useCallback(async (conversationId: string) => {
    setIsLoading(true);
    try {
      const token = getToken();
      const res = await fetch(`${API_BASE}/chat/conversations/${conversationId}/messages`, {
        headers: chatAuthHeaders(token),
      });
      if (res.ok) {
        const data = await res.json();
        setMessages(
          (data.messages || []).map((m: any) => ({
            id: m.id,
            role: m.role as "user" | "assistant",
            content: m.content,
            actions: m.event_metadata?.map((evt: any, i: number) => ({
              id: `history-${m.id}-${i}`,
              action: evt.action,
              data: evt,
            })),
          }))
        );
        setActions([]);
      }
    } catch {
      // Silent
    } finally {
      setIsLoading(false);
    }
  }, [getToken]);

  const sendMessage = useCallback(async (content: string, fileMeta?: UploadedFile) => {
    if (!content.trim() || isStreaming) return;

    let fullContent = content.trim();

    // Append file metadata if provided
    if (fileMeta) {
      fullContent += `\n\n[Attached PDF: ${fileMeta.filename} | file_ref: ${fileMeta.file_ref} | storage_key: ${fileMeta.storage_key}]`;
    }

    setError(null);
    setActions([]); // Clear live actions from previous round
    setStatusText(null);

    // Ensure we have a conversation
    let convId = activeConversationId;
    if (!convId) {
      convId = await createConversation();
      if (!convId) {
        setError("Could not start conversation. Please try again.");
        return;
      }
    }

    // Add user message immediately
    const userMsg: ChatMessage = {
      id: `user-${Date.now()}`,
      role: "user",
      content: fullContent,
    };
    setMessages((prev) => [...prev, userMsg]);
    setIsStreaming(true);

    // Track analytics
    if (typeof window !== "undefined" && (window as any).sfAnalytics?.track) {
      (window as any).sfAnalytics.track("chat_message_sent", "Chat");
    }

    try {
      const controller = new AbortController();
      abortControllerRef.current = controller;
      const token = getToken();

      // Build page context with extra context
      let pageContext = pathname || "";
      if (options.extraContext) {
        pageContext += ` [context: ${JSON.stringify(options.extraContext)}]`;
      }

      const res = await fetch(`${API_BASE}/chat/conversations/${convId}/messages`, {
        method: "POST",
        headers: {
          ...chatAuthHeaders(token),
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ content: fullContent, page_context: pageContext, page_state: getPageState() }),
        signal: controller.signal,
      });

      if (!res.ok) {
        if (res.status === 429) {
          setError("Too many messages. Please wait a moment.");
        } else {
          setError("Failed to send message. Please try again.");
        }
        setIsStreaming(false);
        return;
      }

      // Read SSE stream
      const reader = res.body?.getReader();
      if (!reader) {
        setIsStreaming(false);
        return;
      }

      const decoder = new TextDecoder();
      let assistantContent = "";
      let buffer = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop() || ""; // Keep incomplete line

        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          const jsonStr = line.slice(6).trim();
          if (!jsonStr) continue;

          try {
            const event = JSON.parse(jsonStr);

            const opts = optionsRef.current;
            if (event.type === "text") {
              setStatusText(null);
              opts.onStatus?.(null);
              assistantContent += event.content;
              setMessages((prev) => {
                const lastMsg = prev[prev.length - 1];
                if (lastMsg?.role === "assistant" && lastMsg.id.startsWith("stream-")) {
                  return [
                    ...prev.slice(0, -1),
                    { ...lastMsg, content: assistantContent },
                  ];
                }
                return [
                  ...prev,
                  { id: `stream-${Date.now()}`, role: "assistant", content: assistantContent },
                ];
              });
            } else if (event.type === "action") {
              const actionEvent: ActionEvent = {
                id: `action-${Date.now()}-${Math.random()}`,
                action: event.action,
                data: event,
              };
              setActions((prev) => [...prev, actionEvent]);

              // Handle special actions via callbacks
              if (event.action === "document_revised" && opts.onDocumentRevised) {
                opts.onDocumentRevised(event.markdown, event.version);
              }
              if (event.action === "navigate_to" && opts.onNavigateTo && event.url) {
                opts.onNavigateTo(ensureOrgHref(orgSlug, event.url));
              }
              if (event.action === "page_settings_updated" && opts.onPageSettingsUpdated && event.page_settings) {
                opts.onPageSettingsUpdated(event.page_settings);
              }
              // Amanda drives the UI: pages register handlers per route via
              // useAmandaUiAction; a page with no handler ignores the event.
              if (event.action === "ui_action" && typeof window !== "undefined") {
                try {
                  window.dispatchEvent(new CustomEvent("amanda:ui_action", {
                    detail: { name: event.name, params: event.params || {} },
                  }));
                } catch {
                  // SSR / dispatch failed — silent no-op
                }
              }

              // Track specific actions
              if (typeof window !== "undefined" && (window as any).sfAnalytics?.track) {
                if (event.action === "discount_offered") {
                  (window as any).sfAnalytics.track("chat_discount_offered", "Chat", event.code);
                } else if (event.action === "escalated") {
                  (window as any).sfAnalytics.track("chat_escalated", "Chat");
                } else if (event.action === "document_generated") {
                  (window as any).sfAnalytics.track("chat_doc_generated", "Chat");
                } else if (event.action === "trial_granted") {
                  (window as any).sfAnalytics.track("chat_trial_granted", "Chat", event.plan);
                }
              }
            } else if (event.type === "card") {
              // V2 typed card — rendered by AmandaCard through ActionCard's
              // `action === "card"` branch.
              setActions((prev) => [
                ...prev,
                { id: `card-${Date.now()}-${Math.random()}`, action: "card", data: event },
              ]);
              opts.onCard?.(event);
              if (
                event.kind === "prepared_action" &&
                typeof window !== "undefined" &&
                (window as any).sfAnalytics?.track
              ) {
                (window as any).sfAnalytics.track("chat_prepared_action", "Chat", event.action);
              }
            } else if (event.type === "status") {
              const text = typeof event.text === "string" ? event.text : null;
              setStatusText(text);
              opts.onStatus?.(text);
            } else if (event.type === "error") {
              setStatusText(null);
              opts.onStatus?.(null);
              setError(event.message || "Something went wrong.");
              // A remembered id that the server no longer knows must not be
              // retried forever — forget it; the next send starts fresh.
              if (event.message === "Conversation not found") {
                forgetConversation();
                setActiveConversationId(null);
              }
            } else if (event.type === "done") {
              setStatusText(null);
              opts.onStatus?.(null);
            }
          } catch {
            // Skip malformed JSON
          }
        }
      }

      // Refresh quota after sending
      loadQuota();
    } catch (err: unknown) {
      if (err instanceof DOMException && err.name === "AbortError") {
        // User stopped — not an error
      } else {
        setError("Network error. Please try again.");
      }
    } finally {
      abortControllerRef.current = null;
      setIsStreaming(false);
      setStatusText(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isStreaming, activeConversationId, createConversation, getToken, pathname, loadQuota, orgSlug, getPageState]);

  const stopStreaming = useCallback(() => {
    abortControllerRef.current?.abort();
    abortControllerRef.current = null;
    setIsStreaming(false);
    setStatusText(null);
  }, []);

  const newConversation = useCallback(() => {
    setActiveConversationId(null);
    setMessages([]);
    setActions([]);
    setError(null);
  }, []);

  const selectConversation = useCallback(async (conversationId: string) => {
    setActiveConversationId(conversationId);
    setError(null);
    await loadMessages(conversationId);
  }, [loadMessages]);

  const deleteConversation = useCallback(async (conversationId: string) => {
    try {
      const token = getToken();
      const res = await fetch(`${API_BASE}/chat/conversations/${conversationId}`, {
        method: "DELETE",
        headers: chatAuthHeaders(token),
      });
      if (res.ok) {
        setConversations((prev) => prev.filter((c) => c.id !== conversationId));
        if (activeConversationId === conversationId) {
          forgetConversation();
          setActiveConversationId(null);
          setMessages([]);
          setActions([]);
        }
        return true;
      }
    } catch {
      // Silent
    }
    return false;
  }, [getToken, activeConversationId]);

  const clearAllConversations = useCallback(async () => {
    try {
      const token = getToken();
      const res = await fetch(`${API_BASE}/chat/conversations`, {
        method: "DELETE",
        headers: chatAuthHeaders(token),
      });
      if (res.ok) {
        forgetConversation();
        setConversations([]);
        setActiveConversationId(null);
        setMessages([]);
        setActions([]);
        return true;
      }
    } catch {
      // Silent
    }
    return false;
  }, [getToken]);

  return {
    // State
    messages,
    actions,
    isStreaming,
    statusText,
    isLoading,
    quota,
    error,
    conversations,
    isUploading,
    activeConversationId,
    isAuthenticated,

    // Actions
    sendMessage,
    stopStreaming,
    newConversation,
    resumeConversation,
    selectConversation,
    deleteConversation,
    clearAllConversations,
    loadQuota,
    loadConversations,
    loadMessages,
    uploadFile,
    setError,
  };
}
